/**
 * UserPerformancePage — 用户行为分析 + 绩效 Dashboard
 * Route: /admin/user-performance
 *
 * api.get<T> 返回 body.data（已解包），T 是内层数据类型：
 *   GET /api/v1/analytics/user-performance → { period_days, users }
 *   GET /api/v1/analytics/events           → FeatureEvent[]
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, Loader2, Users, Zap, AlertTriangle, TrendingUp,
  Bot, BarChart3, Activity, Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserPerf {
  user_id: string;
  tenant_id: string;
  user_display_name: string | null;
  total_actions: string;
  unique_features: string;
  active_days: string;
  ai_actions: string;
  ai_adoption_pct: number;
  last_active_at: string | null;
  last_page_view: string | null;
  is_inactive: boolean;
}

interface FeatureEvent {
  id: string;
  user_display_name: string | null;
  feature: string;
  action: string;
  outcome: string | null;
  app: string;
  created_at: string;
}

// api.get<T> returns body.data — T is the inner data shape, NOT the envelope
interface PerfData {
  period_days: number;
  users: UserPerf[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('admin.userPerf.justNow');
  if (m < 60) return t('admin.userPerf.minutesAgo', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('admin.userPerf.hoursAgo', { count: h });
  return t('admin.userPerf.daysAgo', { count: Math.floor(h / 24) });
}

const FEATURE_LABELS: Record<string, string> = {
  'hms.job': 'HMS Job',
  'tms.trip': 'TMS Trip',
  'ai.chat': 'AI Chat',
  'ai.query': 'AI Query',
  'finance.ledger': 'Finance Ledger',
  'wms.receive': 'WMS Receive',
  'document': 'Document',
  'report': 'Report',
  'approval': 'Approval',
};

function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature;
}

const OUTCOME_COLOR: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  error:   'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

// ── KPI Cards ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon, accent }: {
  label: string; value: string | number; sub?: string;
  icon: React.ReactNode; accent?: string;
}) {
  return (
    <Card className={cn('border', accent)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── User Table ─────────────────────────────────────────────────────────────────

function UserTable({ users, t }: { users: UserPerf[]; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const [sortKey, setSortKey] = useState<keyof UserPerf>('total_actions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = [...users].sort((a, b) => {
    const av = sortDir === 'desc' ? b[sortKey] : a[sortKey];
    const bv = sortDir === 'desc' ? a[sortKey] : b[sortKey];
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av) < String(bv) ? -1 : 1;
  });

  function toggleSort(key: keyof UserPerf) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  function SortTh({ col, label }: { col: keyof UserPerf; label: string }) {
    const active = sortKey === col;
    return (
      <th
        className={cn('px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none whitespace-nowrap', active && 'text-foreground')}
        onClick={() => toggleSort(col)}
      >
        {label} {active ? (sortDir === 'desc' ? '↓' : '↑') : ''}
      </th>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <SortTh col="user_display_name" label={t('admin.userPerf.colUser')} />
            <SortTh col="total_actions" label={t('admin.userPerf.colTotalOps')} />
            <SortTh col="unique_features" label={t('admin.userPerf.colFeatures')} />
            <SortTh col="active_days" label={t('admin.userPerf.colActiveDays')} />
            <SortTh col="ai_adoption_pct" label={t('admin.userPerf.colAiAdoption')} />
            <SortTh col="last_active_at" label={t('admin.userPerf.colLastActive')} />
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t('admin.userPerf.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((u) => (
            <tr key={u.user_id} className={cn('border-b hover:bg-muted/30', u.is_inactive && 'bg-red-50/40')}>
              <td className="px-3 py-2.5 font-medium">
                {u.user_display_name ?? <span className="text-muted-foreground text-xs">{u.user_id.slice(0, 8)}…</span>}
              </td>
              <td className="px-3 py-2.5 tabular-nums">{Number(u.total_actions).toLocaleString()}</td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="tabular-nums">{u.unique_features}</span>
                  <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${Math.min(100, (Number(u.unique_features) / 10) * 100)}%` }}
                    />
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 tabular-nums">{t('admin.userPerf.activeDays', { count: Number(u.active_days) })}</td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className={cn('tabular-nums text-xs font-medium', u.ai_adoption_pct >= 20 ? 'text-green-700' : u.ai_adoption_pct >= 5 ? 'text-yellow-700' : 'text-red-600')}>
                    {u.ai_adoption_pct}%
                  </span>
                  <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', u.ai_adoption_pct >= 20 ? 'bg-green-500' : u.ai_adoption_pct >= 5 ? 'bg-yellow-400' : 'bg-red-400')}
                      style={{ width: `${Math.min(100, u.ai_adoption_pct)}%` }}
                    />
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">{timeAgo(u.last_active_at, t)}</td>
              <td className="px-3 py-2.5">
                {u.is_inactive
                  ? <Badge variant="outline" className="border-red-300 text-red-600 text-xs">{t('admin.userPerf.statusInactive')}</Badge>
                  : <Badge variant="outline" className="border-green-300 text-green-600 text-xs">{t('admin.userPerf.statusActive')}</Badge>}
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground text-sm">{t('admin.userPerf.noData')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Recent Events Feed ────────────────────────────────────────────────────────

function EventFeed({ events, t }: { events: FeatureEvent[]; t: (k: string, opts?: Record<string, unknown>) => string }) {
  return (
    <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
      {events.map((e) => (
        <div key={e.id} className="flex items-center gap-2 text-sm py-1.5 border-b last:border-0">
          <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', OUTCOME_COLOR[e.outcome ?? 'success'] ?? 'bg-gray-100 text-gray-600')}>
            {e.outcome ?? 'ok'}
          </span>
          <span className="font-medium text-xs">{featureLabel(e.feature)}</span>
          <span className="text-muted-foreground text-xs">→</span>
          <span className="text-xs">{e.action}</span>
          {e.user_display_name && (
            <span className="ml-auto text-xs text-muted-foreground truncate max-w-[100px]">{e.user_display_name}</span>
          )}
          <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(e.created_at, t)}</span>
        </div>
      ))}
      {events.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t('admin.userPerf.noData')}</p>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function UserPerformancePage() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserPerf[]>([]);
  const [events, setEvents] = useState<FeatureEvent[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // api.get<T> returns body.data already unwrapped — access fields directly
      const [perfData, eventsData] = await Promise.all([
        api.get<PerfData>(`/analytics/user-performance?days=${days}`),
        api.get<FeatureEvent[]>(`/analytics/events?days=${days}&limit=30`),
      ]);
      setUsers(perfData?.users ?? []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setLastRefresh(new Date());
    } catch {
      // silent — keep stale data visible
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  // 计算摘要指标
  const totalUsers    = users.length;
  const activeUsers   = users.filter(u => !u.is_inactive).length;
  const inactiveUsers = users.filter(u => u.is_inactive).length;
  const avgAiPct      = totalUsers > 0
    ? Math.round(users.reduce((s, u) => s + u.ai_adoption_pct, 0) / totalUsers)
    : 0;
  // 总操作数来自 users 汇总（比 events feed 的 30 条更全面）
  const totalActions  = users.reduce((s, u) => s + Number(u.total_actions), 0);

  // 功能采用热度 Top 5
  const featureHeat: Record<string, number> = {};
  for (const e of events) {
    featureHeat[e.feature] = (featureHeat[e.feature] ?? 0) + 1;
  }
  const topFeatures = Object.entries(featureHeat).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxHeat = topFeatures[0]?.[1] ?? 1;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-500" />
            {t('admin.userPerf.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('admin.userPerf.subtitle')}
            {lastRefresh && <span className="ml-2 text-xs">{t('admin.userPerf.lastRefresh')} {timeAgo(lastRefresh.toISOString(), t)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
          >
            <option value={7}>{t('admin.userPerf.last7')}</option>
            <option value={14}>{t('admin.userPerf.last14')}</option>
            <option value={30}>{t('admin.userPerf.last30')}</option>
            <option value={60}>{t('admin.userPerf.last60')}</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label={t('admin.userPerf.totalUsers')} value={totalUsers} icon={<Users className="h-5 w-5" />} />
        <KpiCard label={t('admin.userPerf.activeUsers')} value={activeUsers} sub={t('admin.userPerf.activeUsersSub')} icon={<TrendingUp className="h-5 w-5" />} accent="border-green-200" />
        <KpiCard label={t('admin.userPerf.riskUsers')} value={inactiveUsers} sub={t('admin.userPerf.riskUsersSub')} icon={<AlertTriangle className="h-5 w-5 text-red-500" />} accent={inactiveUsers > 0 ? 'border-red-200' : undefined} />
        <KpiCard label={t('admin.userPerf.avgAiAdoption')} value={`${avgAiPct}%`} sub={t('admin.userPerf.avgAiAdoptionSub')} icon={<Bot className="h-5 w-5 text-purple-500" />} />
        <KpiCard label={t('admin.userPerf.totalOps')} value={totalActions.toLocaleString()} sub={t('admin.userPerf.days', { count: days })} icon={<BarChart3 className="h-5 w-5" />} />
      </div>

      {/* Body: Feature Heat + Recent Events */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Feature Adoption Heatmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              {t('admin.userPerf.featureHeat')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {topFeatures.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('admin.userPerf.noData')}</p>
            )}
            {topFeatures.map(([feature, count]) => (
              <div key={feature} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">{featureLabel(feature)}</span>
                  <span className="text-muted-foreground">{count} 次</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${(count / maxHeat) * 100}%` }}
                  />
                </div>
              </div>
            ))}

            {/* AI 采用率分布 */}
            {users.length > 0 && (
              <div className="pt-2 border-t mt-4">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <Bot className="h-3.5 w-3.5" /> {t('admin.userPerf.aiDistribution')}
                </p>
                <div className="flex gap-2 text-xs">
                  <div className="flex-1 text-center">
                    <div className="text-lg font-bold text-green-600">{users.filter(u => u.ai_adoption_pct >= 20).length}</div>
                    <div className="text-muted-foreground">{t('admin.userPerf.aiHigh')}</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-lg font-bold text-yellow-600">{users.filter(u => u.ai_adoption_pct >= 5 && u.ai_adoption_pct < 20).length}</div>
                    <div className="text-muted-foreground">{t('admin.userPerf.aiMid')}</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-lg font-bold text-red-600">{users.filter(u => u.ai_adoption_pct < 5).length}</div>
                    <div className="text-muted-foreground">{t('admin.userPerf.aiLow')}</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Events Feed */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              {t('admin.userPerf.recentEvents')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EventFeed events={events} t={t} />
          </CardContent>
        </Card>
      </div>

      {/* User Performance Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('admin.userPerf.userTable')}
            <span className="text-xs text-muted-foreground font-normal ml-1">{t('admin.userPerf.tableSortHint')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <UserTable users={users} t={t} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
